import * as PopoverPrimitive from '@radix-ui/react-popover'
import { Check, ChevronDown, Search } from 'lucide-react'
import {
  type ComponentProps,
  createContext,
  type FocusEventHandler,
  type KeyboardEvent,
  type Ref,
  type RefObject,
  useContext,
  useId,
  useRef,
  useState,
} from 'react'
import { cn } from '../lib/cn'
import { type ControlSize, type ControlVariant, controlClassName } from '../lib/control-styles'
import { FieldContext, useFieldControlProps } from '../lib/field-context'

type ComboboxContextValue = {
  activeId?: string
  contentRef: RefObject<HTMLDivElement | null>
  disabled?: boolean
  displayValue: string
  inputRef: RefObject<HTMLInputElement | null>
  listId: string
  listRef: RefObject<HTMLDivElement | null>
  open: boolean
  query: string
  required?: boolean
  value?: string
  choose: (value: string) => void
  setActiveId: (id?: string) => void
  setOpen: (open: boolean) => void
  setQuery: (query: string) => void
}

const ComboboxContext = createContext<ComboboxContextValue | null>(null)

function useCombobox() {
  const context = useContext(ComboboxContext)
  if (!context) throw new Error('Combobox parts must be used inside Combobox.')
  return context
}

export function Combobox({
  children,
  disabled,
  displayValue = '',
  name,
  onQueryChange,
  onValueChange,
  query,
  required,
  value,
}: {
  children: ComponentProps<typeof PopoverPrimitive.Root>['children']
  disabled?: boolean
  displayValue?: string
  name?: string
  onQueryChange: (query: string) => void
  onValueChange: (value: string) => void
  query: string
  required?: boolean
  value?: string
}) {
  const [open, setOpenState] = useState(false)
  const [activeId, setActiveId] = useState<string>()
  const contentRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  function setOpen(next: boolean) {
    if (disabled && next) return
    setOpenState(next)
    if (!next) {
      setActiveId(undefined)
      onQueryChange('')
    }
  }

  function choose(nextValue: string) {
    onValueChange(nextValue)
    setOpen(false)
    inputRef.current?.focus()
  }

  return (
    <ComboboxContext.Provider
      value={{
        activeId,
        choose,
        contentRef,
        disabled,
        displayValue,
        inputRef,
        listId,
        listRef,
        open,
        query,
        required,
        setActiveId,
        setOpen,
        setQuery: onQueryChange,
        value,
      }}
    >
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        {name ? <input type="hidden" name={name} value={value ?? ''} disabled={disabled} /> : null}
        {children}
      </PopoverPrimitive.Root>
    </ComboboxContext.Provider>
  )
}

type ComboboxInputProps = Omit<
  ComponentProps<'input'>,
  'defaultValue' | 'onChange' | 'role' | 'type' | 'value'
> & {
  controlSize?: ControlSize
  variant?: ControlVariant
}

export function ComboboxInput({
  className,
  controlSize = 'md',
  onBlur,
  onFocus,
  onKeyDown,
  ref,
  variant = 'outline',
  ...props
}: ComboboxInputProps) {
  const context = useCombobox()
  const fieldContext = useContext(FieldContext)
  const field = useFieldControlProps(props)

  function options() {
    return Array.from(
      context.listRef.current?.querySelectorAll<HTMLElement>(
        '[role="option"]:not([aria-disabled="true"])',
      ) ?? [],
    )
  }

  function activate(direction: 'first' | 'last' | 'next' | 'previous') {
    const available = options()
    if (!available.length) return
    const current = available.findIndex((option) => option.id === context.activeId)
    const index =
      direction === 'first'
        ? 0
        : direction === 'last'
          ? available.length - 1
          : direction === 'next'
            ? current < 0 || current === available.length - 1
              ? 0
              : current + 1
            : current <= 0
              ? available.length - 1
              : current - 1
    const option = available[index]
    context.setActiveId(option?.id)
    option?.scrollIntoView({ block: 'nearest' })
  }

  function openAndActivate(direction: 'first' | 'last' | 'next' | 'previous') {
    if (context.open) {
      activate(direction)
      return
    }
    context.setOpen(true)
    requestAnimationFrame(() => activate(direction))
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    onKeyDown?.(event)
    if (event.defaultPrevented) return
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      openAndActivate(event.key === 'ArrowDown' ? 'next' : 'previous')
      return
    }
    if (context.open && (event.key === 'Home' || event.key === 'End')) {
      event.preventDefault()
      activate(event.key === 'Home' ? 'first' : 'last')
      return
    }
    if (context.open && event.key === 'Enter' && context.activeId) {
      const option = options().find((item) => item.id === context.activeId)
      const nextValue = option?.dataset.value
      if (nextValue) {
        event.preventDefault()
        context.choose(nextValue)
      }
      return
    }
    if (context.open && event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      context.setOpen(false)
    }
  }

  const setInputRef = (node: HTMLInputElement | null) => {
    context.inputRef.current = node
    assignRef(ref, node)
  }
  const handleBlur: FocusEventHandler<HTMLInputElement> = (event) => {
    onBlur?.(event)
    const next = event.relatedTarget
    if (next instanceof Node && context.contentRef.current?.contains(next)) return
    context.setOpen(false)
  }

  return (
    <PopoverPrimitive.Anchor asChild>
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-3.5 z-10 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <input
          {...props}
          {...field}
          ref={setInputRef}
          type="search"
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-controls={context.listId}
          aria-expanded={context.open}
          aria-activedescendant={context.open ? context.activeId : undefined}
          aria-haspopup="listbox"
          aria-labelledby={
            props['aria-labelledby'] ?? (props['aria-label'] ? undefined : fieldContext?.labelId)
          }
          aria-required={context.required || undefined}
          disabled={context.disabled || props.disabled}
          value={context.open ? context.query : context.displayValue}
          data-slot="combobox-input"
          className={controlClassName(
            variant,
            cn('pr-10 pl-10 [&::-webkit-search-cancel-button]:hidden', className),
            controlSize,
          )}
          onBlur={handleBlur}
          onChange={(event) => {
            context.setQuery(event.currentTarget.value)
            context.setActiveId(undefined)
            context.setOpen(true)
          }}
          onClick={() => context.setOpen(true)}
          onFocus={(event) => {
            if (!context.open) context.setQuery('')
            context.setOpen(true)
            onFocus?.(event)
          }}
          onKeyDown={handleKeyDown}
        />
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-muted-foreground transition-transform',
            context.open && 'rotate-180',
          )}
        />
      </div>
    </PopoverPrimitive.Anchor>
  )
}

export function ComboboxContent({
  children,
  className,
  ref,
  ...props
}: ComponentProps<typeof PopoverPrimitive.Content>) {
  const context = useCombobox()
  const setContentRef = (node: HTMLDivElement | null) => {
    context.contentRef.current = node
    assignRef(ref, node)
  }
  return (
    <PopoverPrimitive.Portal>
      <FieldContext.Provider value={null}>
        <PopoverPrimitive.Content
          {...props}
          ref={setContentRef}
          role={props.role ?? 'presentation'}
          data-slot="combobox-content"
          sideOffset={6}
          collisionPadding={12}
          onOpenAutoFocus={(event) => {
            props.onOpenAutoFocus?.(event)
            if (!event.defaultPrevented) event.preventDefault()
          }}
          onCloseAutoFocus={(event) => {
            props.onCloseAutoFocus?.(event)
            if (!event.defaultPrevented) event.preventDefault()
          }}
          onInteractOutside={(event) => {
            props.onInteractOutside?.(event)
            if (event.target === context.inputRef.current) event.preventDefault()
          }}
          className={cn(
            'z-[70] w-[var(--radix-popover-trigger-width)] max-w-[var(--radix-popover-content-available-width)] overflow-hidden rounded-2xl border border-border bg-card text-foreground shadow-[0_14px_36px_rgba(43,35,32,0.14)] outline-none',
            'origin-[var(--radix-popover-content-transform-origin)] motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            className,
          )}
        >
          {children}
        </PopoverPrimitive.Content>
      </FieldContext.Provider>
    </PopoverPrimitive.Portal>
  )
}

export function ComboboxList({
  className,
  onTouchMove,
  onWheel,
  ref,
  ...props
}: Omit<ComponentProps<'div'>, 'role'>) {
  const context = useCombobox()
  const setListRef = (node: HTMLDivElement | null) => {
    context.listRef.current = node
    assignRef(ref, node)
  }
  return (
    <div
      {...props}
      ref={setListRef}
      id={context.listId}
      role="listbox"
      data-slot="combobox-list"
      className={cn('max-h-72 overflow-y-auto overscroll-contain p-1.5', className)}
      onTouchMove={(event) => {
        onTouchMove?.(event)
        if (!event.defaultPrevented) event.stopPropagation()
      }}
      onWheel={(event) => {
        onWheel?.(event)
        if (!event.defaultPrevented) event.stopPropagation()
      }}
    />
  )
}

export function ComboboxOption({
  children,
  className,
  disabled,
  onKeyDown,
  onMouseDown,
  onMouseEnter,
  value,
  ...props
}: Omit<ComponentProps<'div'>, 'onClick' | 'role'> & {
  disabled?: boolean
  value: string
}) {
  const context = useCombobox()
  const id = useId()
  const active = context.activeId === id
  const selected = context.value === value
  return (
    <div
      {...props}
      id={id}
      role="option"
      tabIndex={-1}
      data-value={value}
      data-active={active || undefined}
      aria-disabled={disabled || undefined}
      aria-selected={selected}
      data-slot="combobox-option"
      className={cn(
        'relative flex min-h-11 cursor-default select-none items-center rounded-xl py-2.5 pe-10 ps-3 text-sm outline-none transition-colors data-active:bg-accent data-active:text-accent-foreground aria-disabled:pointer-events-none aria-disabled:opacity-55',
        selected && 'bg-accent/60',
        className,
      )}
      onClick={() => !disabled && context.choose(value)}
      onKeyDown={(event) => {
        onKeyDown?.(event)
        if (!event.defaultPrevented && !disabled && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault()
          context.choose(value)
        }
      }}
      onMouseDown={(event) => {
        onMouseDown?.(event)
        if (!event.defaultPrevented) event.preventDefault()
      }}
      onMouseEnter={(event) => {
        onMouseEnter?.(event)
        if (!disabled) context.setActiveId(id)
      }}
    >
      <span className="min-w-0 flex-1">{children}</span>
      {selected ? (
        <Check aria-hidden="true" className="absolute right-3 size-4 shrink-0 text-action" />
      ) : null}
    </div>
  )
}

export function ComboboxEmpty({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      {...props}
      role="presentation"
      data-slot="combobox-empty"
      className={cn('px-3 py-5 text-center text-sm text-muted-foreground', className)}
    />
  )
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') ref(value)
  else if (ref) ref.current = value
}
